'use client';

import type { IProductCategory } from '@/types';

import React from 'react';
import { ChevronLeft, ChevronRight } from 'react-feather';
import { FreeMode } from 'swiper/modules';
import {
  Swiper,
  type SwiperClass,
  type SwiperRef,
  SwiperSlide,
} from 'swiper/react';

import {
  Box,
  Button,
  Container,
  Icon,
  IconButton,
  Link,
  Stack,
  Text,
} from '@/components';
import { useTranslation } from '@/hooks';

type CategoriesSwiperProps = {
  categories: IProductCategory[];
  hrefCreator: (categoryCode: number) => string;
  initialSelectedCategory?: number | null;
};

const CategoriesSwiper: React.FC<CategoriesSwiperProps> = ({
  categories,
  hrefCreator,
  initialSelectedCategory,
}) => {
  const { t } = useTranslation('pages.categories');

  const swiperRef = React.useRef<SwiperRef>(null);
  const [progress, setProgress] = React.useState(0);
  const [selectedCategory, setSelectedCategory] = React.useState<number | null>(
    initialSelectedCategory || null
  );

  const prevSlide = () => {
    swiperRef.current?.swiper.slidePrev();
  };

  const nextSlide = () => {
    swiperRef.current?.swiper.slideNext();
  };

  const handleSlideChange = (event: SwiperClass) => {
    setProgress(event.progress);
  };

  return (
    <Container
      mt={{ base: 2.5, md: 8 }}
      mb={{ base: '30px', md: '22px' }}
      paddingInline={{ base: 0, md: 2.5 }}
    >
      <Stack
        flexDir="row"
        justifyContent="space-between"
        alignItems="center"
        display={{ base: 'flex', md: 'none' }}
        px={2.5}
        mb={4}
      >
        <Text minW="max-content">{t('productsCategories')}:</Text>
        <Box flexShrink={0} pr={10}>
          <IconButton
            isDisabled={progress === 0}
            variant="unstyled"
            aria-label="previous"
            icon={<Icon boxSize="6" as={ChevronRight} stroke={'text'} />}
            onClick={() => {
              prevSlide();
            }}
          />
          <IconButton
            isDisabled={progress === 1}
            variant="unstyled"
            aria-label="next"
            icon={<Icon boxSize="6" as={ChevronLeft} stroke={'text'} />}
            marginInlineStart="16px"
            onClick={() => {
              nextSlide();
            }}
          />
        </Box>{' '}
      </Stack>
      <Stack flexDir="row" align="center" gap={3} fontWeight={500}>
        <Text display={{ base: 'none', md: 'block' }} minW="max-content">
          {t('productsCategories')}:
        </Text>
        <Box
          overflow="hidden"
          sx={{
            '.swiper': {
              width: '100%',
            },
            '.swiper-slide': { width: 'fit-content' },
            '.swiper-wrapper': { display: 'flex' },
          }}
        >
          <Swiper
            watchSlidesProgress={true}
            freeMode
            slidesPerView="auto"
            dir="rtl"
            spaceBetween="0"
            onSlideChange={handleSlideChange}
            ref={swiperRef}
            modules={[FreeMode]}
          >
            {categories?.map((category, index) => (
              <SwiperSlide key={category.id}>
                <Button
                  key={category.id}
                  ms={{
                    base: index === 0 ? '10px' : '5px',
                    md: index === 0 ? 0 : '5px',
                  }}
                  me={{
                    base: index + 1 === categories.length ? '10px' : '5px',
                    md: index + 1 === categories.length ? 0 : '5px',
                  }}
                  as={Link}
                  href={hrefCreator(category.code)}
                  onClick={() => {
                    setSelectedCategory(category.code);
                  }}
                  minW="max-content !important"
                  colorScheme="secondary"
                  bg={
                    selectedCategory === category.code
                      ? 'secondary.500'
                      : 'secondary.50'
                  }
                  size={{ base: 'sm', md: 'md' }}
                  fontSize={{ base: 'xs !important', md: 'md !important' }}
                  color="primary.500"
                  _hover={{ bg: 'secondary.500' }}
                  _active={{
                    bg: 'secondary.50',
                    _hover: { bg: 'secondary.500' },
                  }}
                >
                  {category.title}
                </Button>
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      </Stack>
    </Container>
  );
};
export default CategoriesSwiper;
